import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Modal } from 'rsuite';
import { useForm, Controller } from 'react-hook-form';
import { client } from 'utilities/db';
import { RenderIf } from 'utilities/helpers';
import { useFetchInventoryHook } from 'hooks/inventory/useFetchInventoryHook';
import { useFetchProductsHook } from 'hooks/products/useFetchProductsHook';
import { useFetchProductsGroupHook } from 'hooks/products/useFetchProductsGroupHook';
import { useFetchProductsCategoryHook } from 'hooks/products/useFetchProductsCategoryHook';
import { useFetchBranches, useFetchBrands } from 'hooks/resourceHooks';
import { CustomTable } from 'components/datatable';
import { PrimaryButton } from 'components/button';
import { TextField } from 'components/input';
import { CustomSelectDropdown } from 'components/select/multi';
import { Toasts } from 'components/toasts';
import { useStore } from 'store';
import { useCountry } from 'contexts/CountryContext';
import EmptyInventoryImg from './../../assets/Images/Empty_inventory.png';
import { Ikon } from 'assets/icons';
import styles from './index.module.css';
import { AccessDenied } from 'components/acess-denied';
import moment from 'moment';

const { Column, HeaderCell, Cell } = Table;

// Waste/Consume Modal Component
const WasteConsumeModal = ({
  isOpen,
  onClose,
  type,
  productData,
  onSubmit,
  wasteConsumeLoading
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset
  } = useForm({
    defaultValues: {
      quantity: '',
      reason: '',
      unit: ''
    }
  });

  const units = [
    'kg',
    'pounds',
    'grams',
    'ounces',
    'liters',
    'teaspoon',
    'number/pieces',
    'tablespoon',
    'dozen'
  ];

  const handleFormSubmit = (data) => {
    onSubmit(data, type);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>
          {type === 'waste' ? 'Waste Item' : 'Consume Item'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600'
              }}
            >
              Product: {productData?.product_name}
            </label>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#666'
              }}
            >
              Current Stock: {productData?.quantity} {productData?.unit}
            </label>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={`${type === 'waste' ? 'Waste' : 'Consume'} Quantity *`}
              placeholder="Enter quantity"
              name="quantity"
              type="number"
              error={
                errors?.quantity?.type === 'required'
                  ? 'Quantity is required'
                  : errors?.quantity?.type === 'min'
                  ? 'Quantity must be greater than 0'
                  : ''
              }
              register={register}
              validation={{
                required: true,
                min: {
                  value: 0.01,
                  message: 'Quantity must be greater than 0'
                }
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Controller
              name="unit"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomSelectDropdown
                  hookForm={true}
                  field={field}
                  label="Unit *"
                  placeholder="Choose unit"
                  options={units?.map((unit) => ({
                    value: unit,
                    label: unit?.toUpperCase()
                  }))}
                  error={
                    errors?.unit?.type === 'required' ? 'Unit is required' : ''
                  }
                />
              )}
            />
          </div>

          <RenderIf isTrue={type === 'waste'}>
            <div style={{ marginBottom: '16px' }}>
              <Controller
                name="reason"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CustomSelectDropdown
                    hookForm={true}
                    field={field}
                    label="Reason *"
                    placeholder="Select reason"
                    options={[
                      { value: 'spoilage', label: 'Spoilage' },
                      { value: 'recipe prep error', label: 'Recipe/prep error' },
                      { value: 'cooking error', label: 'Cooking error' },
                      {
                        value: 'cross contamination',
                        label: 'Cross-contamination'
                      },
                      { value: 'expired product', label: 'Expired product' },
                      {
                        value: 'spillage breakage',
                        label: 'Spillage / breakage'
                      },
                      { value: 'shrink theft', label: 'Shrink/theft' },
                      { value: 'others', label: 'Others' }
                    ]}
                    error={
                      errors?.reason?.type === 'required'
                        ? 'Reason is required'
                        : ''
                    }
                  />
                )}
              />
            </div>
          </RenderIf>

          <div
            style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}
          >
            <Button onClick={handleClose} appearance="subtle">
              Cancel
            </Button>
            <Button
              type="submit"
              appearance="primary"
              disabled={wasteConsumeLoading}
            >
              {type === 'waste' ? 'Waste Item' : 'Consume Item'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

const ProductCell = ({ rowData, dataKey, ...props }) => {
  const navigate = useNavigate();
  return (
    <Cell {...props} className={styles.product_name}>
      <button
        type={`button`}
        style={{
          background: `transparent`,
          height: `auto`,
          padding: 0,
          border: 0,
          borderRadius: 0,
          outline: `none`,
          boxShadow: `none`,
          cursor: `pointer`
        }}
        onClick={() => navigate(`/inventory/product/${rowData?.product_id}`)}
      >
        {rowData?.product_name}
      </button>
    </Cell>
  );
};

const StockStatusCell = ({ rowData, dataKey, ...props }) => {
  const status = rowData?.low_stock;

  const getBadgeClass = (status) => {
    if (status === 'Low Stock') {
      return styles.stockBadgeLow;
    } else if (status === 'No problem here') {
      return styles.stockBadgeGood;
    } else {
      return styles.stockBadgeWarning;
    }
  };

  return (
    <Cell {...props}>
      <span className={`${styles.stockBadge} ${getBadgeClass(status)}`}>
        {status}
      </span>
    </Cell>
  );
};

export const InventoryPage = () => {
  const { brand, user, branch } = useStore((state) => state);
  const { currencySymbol, formatDate } = useCountry();
  const navigate = useNavigate();

  const [searchField, setSearchField] = useState('');
  const [activeView, setActiveView] = useState('all'); // 'all' or 'low_stock'
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(''); // 'waste' or 'consume'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [wasteConsumeLoading, setWasteConsumeLoading] = useState(false);

  const { inventoryData, inventoryLoading } = useFetchInventoryHook(
    `inventory/${branch?.id}`,
    branch?.id
  );

  const expiredInventoryData = inventoryData?.expiredInventories;
  console.log(
    '🚀 ~ InventoryPage ~ expiredInventoryData:',
    expiredInventoryData
  );

  const { productsData, productsLoading } = useFetchProductsHook(
    `products/${brand?.id}/${branch?.id}`,
    brand?.id,
    branch?.id
  );
  const { productCategoryData, productCategoryLoading } =
    useFetchProductsCategoryHook('products-categories', branch?.id);
  const { productGroupData, productGroupLoading } = useFetchProductsGroupHook(
    'products-groups',
    branch?.id
  );

  const { data: branchesData, isLoading: branchesLoading } = useFetchBranches(
    brand?.id
  );

  const { data: brandsData, isLoading: brandsLoading } = useFetchBrands(
    user?.id
  );

  const { inventoryRefetch } = useFetchInventoryHook(
    `inventory/${branch?.id}`,
    branch?.id
  );

  const handleWasteConsume = (rowData, type) => {
    setSelectedProduct(rowData);
    setModalType(type);
    setModalOpen(true);
  };

  const handleModalSubmit = async (formData, type) => {
    try {
      setWasteConsumeLoading(true);
      const payload = {
        product_id: selectedProduct?.product_id,
        quantity: Number(formData.quantity),
        unit: formData.unit?.value,
        reason: type === 'consume' ? '' : formData.reason?.value,
        branch_id: branch?.id
      };

      if (modalType === 'waste') {
        await client.post(
          `/inventory/wasted/${selectedProduct?.product_id}`,
          payload
        );
      } else {
        await client.post(
          `/inventory/consume/${selectedProduct?.product_id}`,
          payload
        );
      }

      Toasts.success(
        `${
          modalType === 'waste' ? 'Item wasted' : 'Item consumed'
        } successfully`
      );
      inventoryRefetch();
      setModalOpen(false);
      setSelectedProduct(null);
      setModalType('');
    } catch (error) {
      Toasts.error(error?.response?.data?.message || 'An error occurred');
    } finally {
      setSelectedProduct(null);
      setWasteConsumeLoading(false);
    }
  };

  const filteredData = inventoryData?.inventories?.filter((item) => {
    return (
      productsData
        ?.find((product) => product?.id === item?.product_id)
        ?.product_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      item?.unit?.toLowerCase()?.includes(searchField?.toLowerCase()) ||
      productCategoryData
        ?.find(
          (productCategory) => productCategory?.id === item?.product_category_id
        )
        ?.category_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      productGroupData
        ?.find((productGroup) => productGroup?.id === item?.product_group_id)
        ?.group_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      brandsData
        ?.find((brand) => brand?.id === item?.brand_id)
        ?.brand_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      branchesData
        ?.find((branch) => branch?.id === item?.branch_id)
        ?.branch_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase())
    );
  });

  const filteredExpiredData = expiredInventoryData?.filter((item) => {
    return (
      productsData
        ?.find((product) => product?.id === item?.product_id)
        ?.product_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      item?.unit?.toLowerCase()?.includes(searchField?.toLowerCase()) ||
      productCategoryData
        ?.find(
          (productCategory) => productCategory?.id === item?.product_category_id
        )
        ?.category_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      productGroupData
        ?.find((productGroup) => productGroup?.id === item?.product_group_id)
        ?.group_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      brandsData
        ?.find((brand) => brand?.id === item?.brand_id)
        ?.brand_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase()) ||
      branchesData
        ?.find((branch) => branch?.id === item?.branch_id)
        ?.branch_name?.toLowerCase()
        ?.includes(searchField?.toLowerCase())
    );
  });

  // Determine which data to show based on active view
  const tableData =
    activeView === 'low_stock' ? filteredExpiredData : filteredData;

  console.log(`tableData`, tableData);

  console.log(`user`, user);
  const inventoryPermission = user?.role?.permissions?.find(
    (permission) =>
      permission?.module_slug === 'inventory' &&
      permission?.slug === 'inventory_management'
  );

  return (
    <>
      <RenderIf isTrue={!inventoryPermission?.access?.read}>
        <AccessDenied />
      </RenderIf>
      <RenderIf isTrue={inventoryPermission?.access?.read}>
        <header className={styles.header}>
          <div>
            <h2
              style={{
                fontSize: '24px',
                fontWeight: '800'
                // margin: '20px 0px'
              }}
            >
              Inventory
            </h2>
          </div>
          <div>
            <PrimaryButton
              onClick={() => {
                navigate(`/inventory/add`);
              }}
              disabled={!inventoryPermission?.access?.create}
            >
              <Ikon name={`plus`} fillColor={`var(--white)`} />
              {inventoryPermission?.access?.create
                ? 'Add inventory'
                : 'Request Access'}
            </PrimaryButton>
          </div>
        </header>

        {/* <InventoryOverviewPage /> */}

        <div className={styles.content}>
          <RenderIf isTrue={inventoryData?.inventories?.length === 0}>
            <div className={styles.empty_state}>
              {/* <h2>You currently have no items added to your inventory</h2> */}
              <RenderIf isTrue={productsData?.length === 0}>
                <p>
                  1. Add products first{' '}
                  <a onClick={() => navigate(`/inventory/products`)}>
                    click here
                  </a>
                </p>
              </RenderIf>
              <p>
                {productsData?.length === 0 ? '2. ' : ''}
                Click Add Inventory to record your first batch (quantity, unit,
                expiry).
              </p>

              <picture>
                <source srcSet={EmptyInventoryImg} />
                <img src={EmptyInventoryImg} alt={`Empty Inventory`} />
              </picture>
              <PrimaryButton
                onClick={() => {
                  navigate(`/inventory/add`);
                }}
                disabled={!inventoryPermission?.access?.create}
              >
                <Ikon name={`plus`} fillColor={`var(--white)`} />
                {inventoryPermission?.access?.create
                  ? 'Add inventory'
                  : 'Request Access'}
              </PrimaryButton>
            </div>
          </RenderIf>
          <RenderIf isTrue={inventoryData?.inventories?.length > 0}>
            <div className={styles.rowEqCol}>
              <div>
                <button
                  className={`${styles.statBx} ${
                    activeView === 'all' ? styles.activeStat : ''
                  }`}
                  onClick={() => setActiveView('all')}
                >
                  <div>
                    <div className={styles.statIcon}>
                      <Ikon name={`all-products`} fillColor={`var(--black)`} />
                    </div>
                  </div>
                  <div>
                    <div className={styles.statLabel}>
                      All Items(s) in Stock
                    </div>
                    <div className={styles.statValue}>
                      {filteredData?.length}
                    </div>
                  </div>
                </button>
              </div>
              <div>
                <button
                  className={`${styles.statBx} ${
                    activeView === 'low_stock' ? styles.activeStat : ''
                  }`}
                  onClick={() => setActiveView('low_stock')}
                >
                  <div>
                    <div className={styles.statIcon}>
                      <Ikon name={`users`} />
                    </div>
                  </div>
                  <div>
                    <div className={styles.statLabel}>Expired Item(s)</div>
                    <div className={styles.statValue}>
                      {expiredInventoryData?.length || 0}
                    </div>
                    <p></p>
                  </div>
                </button>
              </div>
            </div>
            <div style={{ marginTop: 24 }}></div>
            <div className={styles.contentBx}>
              <div className={styles.contentHeader}>
                <h6>
                  <span className="ff_semi">Inventory Items</span>
                </h6>
                <div>
                  <div className={styles.searchBx}>
                    <button type={`button`} className={styles.searchButton}>
                      <Ikon name={`search`} />
                    </button>
                    <TextField
                      onChange={(e) => setSearchField(e?.target?.value)}
                      value={searchField}
                      placeholder={'Search'}
                    />
                  </div>
                </div>
              </div>
              <CustomTable
                rows={
                  tableData?.map((inv, index) => {
                    return {
                      id: index + 1,
                      product_name:
                        productsData?.find(
                          (product) => product?.id === inv?.product_id
                        )?.product_name || '-',
                      product_category:
                        productCategoryData?.find(
                          (productCategory) =>
                            productCategory?.id === inv?.product_category_id
                        )?.category_name || '-',
                      product_group:
                        productGroupData?.find(
                          (productGroup) =>
                            productGroup?.id === inv?.product_group_id
                        )?.group_name || '-',
                      unit_price:
                        currencySymbol +
                          parseFloat(inv?.unit_price)?.toFixed(4) || '-',
                      unit: inv?.converted_unit || inv?.unit || '-',
                      quantity: inv?.converted_quantity || inv?.quantity || '-',
                      total_price:
                        currencySymbol +
                          parseFloat(
                            parseFloat(inv?.unit_price)?.toFixed(4) *
                              (inv?.converted_quantity || inv?.quantity)
                          ).toFixed(2) || '-',
                      low_stock:
                        activeView === 'low_stock'
                          ? 'Low Stock'
                          : inv?.low_stock
                          ? 'Low Stock'
                          : 'No problem here',
                      product_id: inv?.product_id,
                      expiry_date: formatDate(inv?.expiry_date) || '-'
                      // brand_name:
                      //   brandsData?.find((brand) => brand?.id === inv?.brand_id)
                      //     ?.brand_name || '-',
                      // branch_name:
                      //   branchesData?.find(
                      //     (branch) => branch?.id === inv?.branch_id
                      //   )?.branch_name || '-',
                      // vendor_name:
                      //   vendorData?.find((vendor) => vendor?.id === inv?.vendor_id)
                      //     ?.vendor_information?.name ||
                      //   vendorData?.find((vendor) => vendor?.id === inv?.vendor_id)
                      //     ?.vendor_information?.email,
                      // purchase_date: inv?.purchase_date || '-',
                      // expiry_date: inv?.expiry_date || '-',
                    };
                  }) || []
                }
                loading={
                  productsLoading ||
                  inventoryLoading ||
                  branchesLoading ||
                  brandsLoading ||
                  productCategoryLoading ||
                  productGroupLoading
                }
                rowHeight={45}
                pageLimit={10}
                pagination={tableData?.length > 10 ? true : false}
              >
                <Column width={50} fixed>
                  <HeaderCell>Id</HeaderCell>
                  <Cell dataKey="id" />
                </Column>
                {/* <Link onClick={(e) => navigate(`/inventory/product/${3}`)}> */}
                <Column width={250} sortable>
                  <HeaderCell>Product Name</HeaderCell>
                  <ProductCell
                    dataKey="product_name"
                    style={{
                      fontSize: '14px',
                      textDecoration: 'underline',
                      fontWeight: '800',
                      color: 'var(--primary)',
                      cursor: 'pointer',
                      textDecorationColor: 'var(--primary)',
                      textUnderlineOffset: '10px'
                    }}
                  />
                </Column>
                {/* </Link> */}
                {/* <Column width={100} sortable>
                <HeaderCell>Category</HeaderCell>
                <Cell dataKey="product_category" />
              </Column>
              <Column width={120} sortable>
                <HeaderCell>Group</HeaderCell>
                <Cell dataKey="product_group" />
              </Column> */}
                <Column width={120} sortable>
                  <HeaderCell>Unit Price</HeaderCell>
                  <Cell dataKey="unit_price" />
                </Column>
                <Column width={100} sortable>
                  <HeaderCell>Unit</HeaderCell>
                  <Cell dataKey="unit" />
                </Column>
                <Column width={100} sortable>
                  <HeaderCell>In Stock</HeaderCell>
                  <Cell dataKey="quantity" />
                </Column>
                <Column width={160} sortable>
                  <HeaderCell>Stock Alert</HeaderCell>
                  <StockStatusCell dataKey="low_stock" />
                </Column>
                <Column width={110} sortable>
                  <HeaderCell>Total Price</HeaderCell>
                  <Cell dataKey="total_price" />
                </Column>
                <Column width={110} sortable>
                  <HeaderCell>Expiry Date</HeaderCell>
                  <Cell dataKey="expiry_date" />
                </Column>
                <Column width={200} fixed="right">
                  <HeaderCell>Actions</HeaderCell>
                  <Cell dataKey="action">
                    {(rowData) => (
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'flex-start',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            color: 'var(--primary)',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            border: '1px solid var(--primary)',
                            borderRadius: '4px',
                            backgroundColor: 'transparent'
                          }}
                          onClick={() =>
                            navigate(
                              `/inventory/product/${rowData?.product_id}`
                            )
                          }
                        >
                          View
                        </div>
                        <div
                          style={{
                            color: '#ff6b35',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            border: '1px solid #ff6b35',
                            borderRadius: '4px',
                            backgroundColor: 'transparent'
                          }}
                          onClick={() => handleWasteConsume(rowData, 'waste')}
                        >
                          Waste
                        </div>
                        <div
                          style={{
                            color: '#28a745',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            backgroundColor: 'transparent'
                          }}
                          onClick={() => handleWasteConsume(rowData, 'consume')}
                        >
                          Consume
                        </div>
                      </div>
                    )}
                  </Cell>
                </Column>

                {/* <Column width={100} sortable>
                <HeaderCell>Brand</HeaderCell>
                <Cell dataKey="brand_name" />
              </Column>
              <Column width={100} sortable>
                <HeaderCell>Branch</HeaderCell>
                <Cell dataKey="branch_name" />
              </Column>
              <Column flexGrow={1} sortable>
                <HeaderCell>Vendor</HeaderCell>
                <Cell dataKey="vendor_name" />
              </Column> */}
                {/* <Column width={140} sortable fixed="right">
                <HeaderCell>Purchase Date</HeaderCell>
                <Cell dataKey="purchase_date" />
              </Column> */}
                {/* <Column width={140} sortable fixed="right">
                <HeaderCell>Expiry Date</HeaderCell>
                <Cell dataKey="expiry_date" />
              </Column> */}
              </CustomTable>
            </div>
          </RenderIf>
        </div>
      </RenderIf>

      {/* Waste/Consume Modal */}
      <WasteConsumeModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedProduct(null);
          setModalType('');
        }}
        type={modalType}
        productData={selectedProduct}
        onSubmit={handleModalSubmit}
        wasteConsumeLoading={wasteConsumeLoading}
      />
    </>
  );
};
